// Documentos del proveedor (FCC, NCC, NDC) · docs/108 B6. Registrar la factura (digitada o desde una
// recepción, con sus líneas ya puestas), identificar líneas, conciliar contra lo recibido, resolver
// diferencias, aceptar (firma) o rechazar; una aceptada no se edita: se pide su invalidación (la
// aprueba otra persona) o se corrige con NC/ND. La NC/ND se registra, se imputa (qué corrige cada
// parte) y se acepta. La aceptación deja la deuda en Pago a Proveedores.
// FACTURA DIRECTA (2026-10-19, como la FCC de Random): si la mercadería llegó con la factura, sin OC
// ni guía, se marca «Llegó con esta factura» y en un paso entra al stock de la bodega, se concilia y
// queda aceptada. El flete de la factura se reparte por valor al costo de los productos.
import { useEffect, useState, type ReactNode } from 'react';
import { PanelError } from '../comun/Estados';
import { decimal, entradaDecimal, fecha, pesos, rut } from '../comun/formato';
import { ImprimirComprobante } from './ComprobanteIngreso';
import { useSesion } from '../sesion/SesionContexto';
import { useBodegas } from '../stock/PantallasStock';
import { BuscadorCliente } from '../ventas/BuscadorCliente';
import { BuscadorProducto, type ProductoElegido } from '../ventas/BuscadorProducto';
import { cuatroDecimales, DialogoMotivo, estimar, Historia, hoy } from './comun';
import {
  AvisoDoc,
  CaminoDoc,
  CuerpoDoc,
  DatosDoc,
  EncabezadoDoc,
  Tarjeta,
  type Paso,
} from './FichaDocumento';
import {
  EFECTOS_NOTA,
  NOMBRE_ESTADO_DOC,
  NOMBRE_TIPO_COMPRA,
  useAccionCompras,
  useDevolucion,
  useDevoluciones,
  useFacturaCompra,
  useFacturasCompra,
  useNombreEntidad,
  useNotaCompra,
  useOrdenCompra,
  useRecepcion,
  type FacturaLeida,
  type NotaLeida,
} from './datos';

export interface AccionesDocumento {
  alRegistrado: (documentoId: string, titulo: string) => void;
  alAbrirRecepcion: (recepcionId: string, titulo: string) => void;
  alNuevaNota: (facturaId: string) => void;
  alAbrirFactura: (facturaId: string, titulo: string) => void;
  /** 0131: corregir un ingreso sin factura (abre la digitación con todo puesto). */
  alCorregir?: (documentoId: string, titulo: string) => void;
}

export function DocumentoProveedor({
  clase,
  documentoId,
  recepcionId,
  facturaId,
  ...acciones
}: AccionesDocumento & {
  clase: 'factura' | 'nota';
  documentoId?: string | undefined;
  /** Factura nueva desde una recepción confirmada. */
  recepcionId?: string | undefined;
  /** NC/ND nueva que corrige esta factura. */
  facturaId?: string | undefined;
}) {
  if (!documentoId)
    return (
      <RegistroDocumento
        clase={clase}
        recepcionId={recepcionId}
        facturaId={facturaId}
        alRegistrado={acciones.alRegistrado}
      />
    );
  return clase === 'factura' ? (
    <FichaFactura id={documentoId} {...acciones} />
  ) : (
    <FichaNota id={documentoId} {...acciones} />
  );
}

// ================================================================ registro (factura o nota)

interface LineaDoc {
  clave: string;
  descripcion: string;
  productoId?: string;
  presentacionId?: string;
  sku?: string;
  cantidad: string;
  precio: string;
  ordenId?: string;
  ordenLineaNumero?: number;
}

const TIPOS_FACTURA = [33, 34, 46, 30, 32];
const TIPOS_NOTA = [61, 56, 60, 55];
const EXENTOS = [34, 32];

function RegistroDocumento({
  clase,
  recepcionId,
  facturaId,
  alRegistrado,
}: {
  clase: 'factura' | 'nota';
  recepcionId?: string | undefined;
  facturaId?: string | undefined;
  alRegistrado: AccionesDocumento['alRegistrado'];
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const accion = useAccionCompras(empresaId);
  const recepcion = useRecepcion(empresaId, recepcionId ?? null);
  const factura = useFacturaCompra(empresaId, facturaId ?? null);
  const [tipo, setTipo] = useState(clase === 'factura' ? 33 : 61);
  const [proveedorId, setProveedorId] = useState<string | null>(null);
  const prov = useNombreEntidad(proveedorId);
  const [folio, setFolio] = useState('');
  const [emision, setEmision] = useState(hoy());
  const [lineas, setLineas] = useState<LineaDoc[]>([]);
  const [iva, setIva] = useState<string | null>(null);
  const [adicionalCodigo, setAdicionalCodigo] = useState('');
  const [adicional, setAdicional] = useState('');
  const [razonRef, setRazonRef] = useState('');
  const [buscandoProv, setBuscandoProv] = useState(false);
  const [buscandoProd, setBuscandoProd] = useState<string | null>(null);
  const [referencias, setReferencias] = useState<Array<Record<string, string>>>([]);
  // Factura directa: la mercadería llegó con la factura (entra al stock). Solo factura nueva sin
  // recepción previa; la que viene de una recepción ya entró con su guía.
  const puedeDirecta = clase === 'factura' && !recepcionId;
  const [directa, setDirecta] = useState(puedeDirecta);
  const [bodegaId, setBodegaId] = useState('');
  const [flete, setFlete] = useState('');
  const [agregando, setAgregando] = useState(false);
  useEffect(() => {
    if (!bodegaId && bodegas.lista[0]) setBodegaId(bodegas.lista[0].id);
  }, [bodegas.lista, bodegaId]);

  // Desde la recepción: proveedor, líneas aceptadas al precio de la OC y referencias a OC y recepción.
  useEffect(() => {
    const r = recepcion.data;
    if (!r || lineas.length) return;
    setProveedorId(r.borrador.proveedor_entidad_id);
    setLineas(
      r.lineas
        .filter((l) => Number(l.cantidad_aceptada) > 0)
        .map((l) => ({
          clave: l.id,
          descripcion: l.descripcion,
          productoId: l.producto_id,
          presentacionId: l.presentacion_id,
          sku: l.sku,
          cantidad: decimal(l.cantidad_aceptada).replace(/\./g, ''),
          precio: l.precio_oc ? decimal(l.precio_oc).replace(/\./g, '') : '',
          ...(r.borrador.orden_id && l.orden_linea_numero
            ? { ordenId: r.borrador.orden_id, ordenLineaNumero: l.orden_linea_numero }
            : {}),
        })),
    );
    setReferencias([
      { tipo: 'RECEPCION', recepcionId: r.borrador.id },
      ...(r.borrador.orden_id ? [{ tipo: 'ORDEN_COMPRA', ordenId: r.borrador.orden_id }] : []),
    ]);
  }, [recepcion.data]);

  // NC/ND sobre una factura: el mismo proveedor y la referencia al folio corregido.
  useEffect(() => {
    const f = factura.data;
    if (!f) return;
    setProveedorId(f.documento.proveedor_entidad_id);
    setReferencias([
      {
        tipo: 'DOCUMENTO',
        folio: f.documento.folio ?? f.numeroVisible,
      },
    ]);
  }, [factura.data]);

  const exento = EXENTOS.includes(tipo);
  const netoLinea = (l: LineaDoc) => Math.round(estimar(l.cantidad) * estimar(l.precio));
  const suma = lineas.reduce((s, l) => s + netoLinea(l), 0);
  const fleteFinal = clase === 'factura' ? Math.round(estimar(flete)) : 0;
  const ivaCalculado = exento ? 0 : Math.round((suma + fleteFinal) * 0.19);
  const ivaFinal = iva === null ? ivaCalculado : Math.round(estimar(iva));
  const adicionalFinal = Math.round(estimar(adicional));
  const total = suma + fleteFinal + ivaFinal + adicionalFinal;
  const esDirecta = puedeDirecta && directa;
  const rutEmisor = prov.data?.rut ? `${prov.data.rut}-${prov.data.dv ?? ''}` : null;
  const lineasApi = lineas.map((l) => ({
    descripcion: l.descripcion.trim(),
    ...(l.productoId ? { productoId: l.productoId, presentacionId: l.presentacionId } : {}),
    cantidad: entradaDecimal(l.cantidad, 6),
    precioUnitario: entradaDecimal(l.precio, 6),
    neto: String(netoLinea(l)),
    ...(exento ? { exenta: true } : {}),
    ...(l.ordenId ? { ordenId: l.ordenId, ordenLineaNumero: l.ordenLineaNumero } : {}),
  }));
  const valido =
    !!rutEmisor &&
    /^\d+$/.test(folio.trim()) &&
    lineasApi.length > 0 &&
    lineasApi.every((l) => l.descripcion && l.cantidad && l.precioUnitario) &&
    (!adicionalFinal || /^[A-Z0-9_]{2,40}$/.test(adicionalCodigo)) &&
    (!esDirecta || (!!bodegaId && lineas.every((l) => l.productoId)));

  const registrar = () => {
    const impuestos = [
      ...(ivaFinal ? [{ codigo: 'IVA', tipo: 'iva', tasa: '0.19', monto: String(ivaFinal) }] : []),
      ...(adicionalFinal
        ? [{ codigo: adicionalCodigo, tipo: 'adicional', monto: String(adicionalFinal) }]
        : []),
    ];
    const cuerpo = {
      empresaId,
      identidad: {
        clase: 'TRIBUTARIA',
        tipoSii: tipo,
        folio: Number(folio.trim()),
        rutEmisor,
        rutReceptor: `${empresa!.rut}-${empresa!.dv}`,
      },
      proveedorEntidadId: proveedorId,
      razonSocialEmisor: prov.data!.razon_social,
      fechaEmision: emision,
      fechaRecepcion: hoy(),
      montos: {
        neto: exento ? '0' : String(suma + fleteFinal),
        ...(exento ? { exento: String(suma + fleteFinal) } : {}),
        iva: String(ivaFinal),
        impuestosAdicionales: String(adicionalFinal),
        total: String(total),
      },
      lineas: lineasApi,
      ...(fleteFinal
        ? {
            cargos: [
              {
                tipo: 'FLETE',
                descripcion: 'Flete',
                monto: String(fleteFinal),
                ...(exento ? { exento: true } : {}),
              },
            ],
          }
        : {}),
      ...(impuestos.length ? { impuestos } : {}),
      ...(referencias.length
        ? {
            referencias: referencias.map((r) =>
              razonRef.trim() && r['tipo'] === 'DOCUMENTO' ? { ...r, razon: razonRef.trim() } : r,
            ),
          }
        : {}),
    };
    accion.mutate(
      esDirecta
        ? { ruta: '/compras/facturas/directa', cuerpo: { ubicacionId: bodegaId, factura: cuerpo } }
        : { ruta: clase === 'factura' ? '/compras/facturas' : '/compras/notas', cuerpo },
      {
        onSuccess: (x) => {
          const r = x as { documentoId: string };
          alRegistrado(
            r.documentoId,
            `${clase === 'factura' ? 'FCC' : tipo === 61 || tipo === 60 ? 'NCC' : 'NDC'} ${folio.trim()}`,
          );
        },
      },
    );
  };

  const elegirProducto = (clave: string, p: ProductoElegido) =>
    setLineas((ls) =>
      ls.map((l) =>
        l.clave === clave
          ? {
              ...l,
              productoId: p.producto.id,
              presentacionId: p.presentacion.id,
              sku: p.producto.sku,
              descripcion: l.descripcion || p.producto.nombre,
            }
          : l,
      ),
    );

  return (
    <div className="pantalla documento-compra" data-pantalla={`registro-${clase}`}>
      <div className="encabezado-entidad">
        <span className="ruta-migas">Compras › Documentos del proveedor</span>
        <h2>
          {clase === 'factura'
            ? 'Registrar factura del proveedor'
            : 'Registrar nota de crédito / débito'}
        </h2>
        {recepcion.data && (
          <p className="apagado">
            Desde la recepción {recepcion.data.numeroVisible}: revisa precios y cantidades contra el
            papel.
          </p>
        )}
        {factura.data && (
          <p className="apagado">Corrige la factura {factura.data.numeroVisible}.</p>
        )}
      </div>
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Tipo</span>
          <select
            aria-label="Tipo de documento"
            value={tipo}
            onChange={(e) => setTipo(Number(e.target.value))}
          >
            {(clase === 'factura' ? TIPOS_FACTURA : TIPOS_NOTA).map((t) => (
              <option key={t} value={t}>
                {t} · {NOMBRE_TIPO_COMPRA[t]}
              </option>
            ))}
          </select>
        </label>
        <div className="campo">
          <span className="etiqueta">Proveedor</span>
          <button
            type="button"
            className="selector-cliente"
            aria-label="Elegir proveedor"
            onClick={() => setBuscandoProv(true)}
          >
            {prov.data ? `${prov.data.razon_social} · ${rut(rutEmisor)}` : 'Buscar proveedor…'}
          </button>
        </div>
        <label className="campo campo-corto">
          <span className="etiqueta">Folio</span>
          <input
            aria-label="Folio"
            inputMode="numeric"
            value={folio}
            onChange={(e) => setFolio(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Emisión</span>
          <input
            type="date"
            aria-label="Fecha de emisión"
            value={emision}
            onChange={(e) => setEmision(e.target.value)}
          />
        </label>
      </div>
      {puedeDirecta && (
        <section className="paso-cobro entrada-mercaderia" aria-label="Entrada de mercadería">
          <div className="barra-filtros">
            <div className="campo" role="radiogroup" aria-label="¿Cómo llegó la mercadería?">
              <span className="etiqueta">¿Cómo llegó la mercadería?</span>
              <label className="opcion-radio">
                <input
                  type="radio"
                  name="entrada"
                  checked={directa}
                  onChange={() => setDirecta(true)}
                />
                Llegó con esta factura: <b>entra al stock ahora</b>
              </label>
              <label className="opcion-radio">
                <input
                  type="radio"
                  name="entrada"
                  checked={!directa}
                  onChange={() => setDirecta(false)}
                />
                Llegó o llegará con guía: solo registrar la factura
              </label>
            </div>
            {directa && (
              <label className="campo">
                <span className="etiqueta">Bodega donde entra</span>
                <select
                  aria-label="Bodega donde entra"
                  value={bodegaId}
                  onChange={(e) => setBodegaId(e.target.value)}
                >
                  {bodegas.lista.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {directa && (
            <p className="apagado">
              Al registrar: entra al stock, se cruza con la factura y queda aceptada (nace la deuda
              con el proveedor). Si algo no cuadra, queda para revisarlo en la factura.
            </p>
          )}
        </section>
      )}
      {prov.data && !prov.data.rut && (
        <p className="tono-malo">
          El proveedor no tiene RUT en su ficha: complétalo antes de registrar.
        </p>
      )}
      <table className="grilla compacta" aria-label="Líneas del documento">
        <thead>
          <tr>
            <th style={{ width: '3rem' }}>N°</th>
            <th>Descripción</th>
            <th style={{ width: '11rem' }}>Producto</th>
            <th style={{ width: '7rem' }} className="der">
              Cantidad
            </th>
            <th style={{ width: '8rem' }} className="der">
              Precio neto
            </th>
            <th style={{ width: '8rem' }} className="der">
              Neto
            </th>
            <th style={{ width: '3rem' }} />
          </tr>
        </thead>
        <tbody>
          {lineas.map((l, i) => {
            const set = (campo: 'descripcion' | 'cantidad' | 'precio', v: string) =>
              setLineas((ls) => ls.map((x) => (x.clave === l.clave ? { ...x, [campo]: v } : x)));
            return (
              <tr key={l.clave}>
                <td>{i + 1}</td>
                <td>
                  <input
                    aria-label={`Descripción línea ${i + 1}`}
                    value={l.descripcion}
                    onChange={(e) => set('descripcion', e.target.value)}
                  />
                </td>
                <td>
                  <button type="button" className="enlace" onClick={() => setBuscandoProd(l.clave)}>
                    {l.sku ?? 'sin identificar'}
                  </button>
                </td>
                <td className="der">
                  <input
                    className="entrada-numero"
                    aria-label={`Cantidad línea ${i + 1}`}
                    value={l.cantidad}
                    onChange={(e) => set('cantidad', e.target.value)}
                  />
                </td>
                <td className="der">
                  <input
                    className="entrada-numero"
                    aria-label={`Precio línea ${i + 1}`}
                    value={l.precio}
                    onChange={(e) => set('precio', e.target.value)}
                  />
                </td>
                <td className="der">{pesos(String(netoLinea(l)))}</td>
                <td>
                  <button
                    type="button"
                    className="boton-mini"
                    aria-label={`Quitar línea ${i + 1}`}
                    onClick={() => setLineas((ls) => ls.filter((x) => x.clave !== l.clave))}
                  >
                    ✕
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="barra-filtros">
        <button type="button" className="primario" onClick={() => setAgregando(true)}>
          Agregar producto
        </button>
        <button
          type="button"
          onClick={() =>
            setLineas((ls) => [
              ...ls,
              { clave: String(Date.now()), descripcion: '', cantidad: '1', precio: '' },
            ])
          }
        >
          Agregar línea sin producto
        </button>
        {clase === 'nota' && (
          <label className="campo">
            <span className="etiqueta">Razón de la referencia</span>
            <input
              aria-label="Razón de la referencia"
              value={razonRef}
              onChange={(e) => setRazonRef(e.target.value)}
            />
          </label>
        )}
      </div>
      <dl className="cabecera-od">
        <div>
          <dt>{exento ? 'Exento' : 'Neto'}</dt>
          <dd>{pesos(String(suma))}</dd>
        </div>
        {clase === 'factura' && (
          <div>
            <dt>Flete</dt>
            <dd>
              <input
                className="entrada-numero"
                aria-label="Flete"
                placeholder="0"
                value={flete}
                onChange={(e) => setFlete(e.target.value)}
              />
            </dd>
          </div>
        )}
        <div>
          <dt>IVA</dt>
          <dd>
            <input
              className="entrada-numero"
              aria-label="IVA"
              disabled={exento}
              value={iva ?? String(ivaCalculado)}
              onChange={(e) => setIva(e.target.value)}
            />
          </dd>
        </div>
        <div>
          <dt>Imp. adicional (código)</dt>
          <dd>
            <input
              aria-label="Código impuesto adicional"
              placeholder="ILA20V…"
              value={adicionalCodigo}
              onChange={(e) => setAdicionalCodigo(e.target.value.toUpperCase())}
            />
          </dd>
        </div>
        <div>
          <dt>Imp. adicional ($)</dt>
          <dd>
            <input
              className="entrada-numero"
              aria-label="Monto impuesto adicional"
              value={adicional}
              onChange={(e) => setAdicional(e.target.value)}
            />
          </dd>
        </div>
        <div>
          <dt>Total</dt>
          <dd>
            <b>{pesos(String(total))}</b>
          </dd>
        </div>
      </dl>
      <p className="apagado">
        Digita las cifras del documento tal como vienen; si los totales no cuadran, el sistema lo
        avisa y no lo registra.
      </p>
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-od">
        <button
          type="button"
          className="primario"
          disabled={!valido || accion.isPending}
          onClick={registrar}
        >
          {esDirecta
            ? 'Registrar y entrar al stock'
            : `Registrar ${clase === 'factura' ? 'factura' : 'nota'}`}
        </button>
        {esDirecta && lineas.some((l) => !l.productoId) && (
          <span className="tono-aviso">
            Cada línea debe tener su producto para entrar al stock.
          </span>
        )}
      </div>
      {buscandoProv && (
        <BuscadorCliente
          empresaId={empresaId}
          rol="proveedor"
          alElegir={(c) => {
            setProveedorId(c.id);
            setBuscandoProv(false);
          }}
          alCerrar={() => setBuscandoProv(false)}
        />
      )}
      {agregando && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodegaId || (bodegas.lista[0]?.id ?? '')}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            setLineas((ls) => [
              ...ls,
              {
                clave: String(Date.now()),
                descripcion: p.producto.nombre,
                productoId: p.producto.id,
                presentacionId: p.presentacion.id,
                sku: p.producto.sku,
                cantidad: '1',
                precio: '',
              },
            ]);
            setAgregando(false);
          }}
          alCerrar={() => setAgregando(false)}
        />
      )}
      {buscandoProd && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodegas.lista[0]?.id ?? ''}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            elegirProducto(buscandoProd, p);
            setBuscandoProd(null);
          }}
          alCerrar={() => setBuscandoProd(null)}
        />
      )}
    </div>
  );
}

// ================================================================ ficha de la factura

const SITUACION: Record<string, string> = {
  SIN_MEDIR: 'Sin conciliar',
  CUADRADA: 'Cuadra con lo recibido',
  CON_DIFERENCIAS_ABIERTAS: 'Con diferencias por resolver',
};

/** Las diferencias en palabras de bodega y tesorería (el tipo técnico queda en el backend). */
const TIPO_DIFERENCIA: Record<string, string> = {
  DIFERENCIA_CANTIDAD: 'Cantidad distinta a lo recibido',
  DIFERENCIA_PRECIO: 'Precio distinto al de la OC',
  DIFERENCIA_IMPUESTO: 'Impuesto distinto',
  DIFERENCIA_FLETE: 'Flete distinto',
  PRODUCTO_NO_IDENTIFICADO: 'Producto sin identificar',
  FACTURA_SIN_RECEPCION: 'Facturado sin guía de recepción',
  RECEPCION_SIN_FACTURA: 'Recibido y no facturado',
  SOBRE_RECEPCION: 'Llegó más de lo pedido',
  SUB_RECEPCION: 'Llegó menos de lo pedido',
  DOCUMENTO_DUPLICADO: 'Documento duplicado',
  TOTAL_DOCUMENTAL_INVALIDO: 'Los totales no cuadran',
  LINEA_NO_TRATADA_D83: 'Línea sin tratar',
  RECEPCION_SIN_OC_PENDIENTE: 'Recepción sin OC pendiente',
  OTRA: 'Otra diferencia',
};

const ESTADO_DIFERENCIA: Record<string, [string, string]> = {
  ABIERTA: ['Por resolver', 'tono-aviso'],
  RECLAMADA: ['Reclamada · espera NC', 'tono-info'],
  ACEPTADA: ['Aceptada', 'tono-ok'],
  AUTOACEPTADA: ['Dentro de la tolerancia', 'tono-ok'],
  INFORMATIVA: ['Informativa', 'tono-neutro'],
  RECHAZADA: ['Rechazada', 'tono-malo'],
};

const TONO_ESTADO_DOC: Record<string, string> = {
  REGISTRADA: 'tono-info',
  ACEPTADA: 'tono-ok',
  RECHAZADA: 'tono-malo',
};

const SIGLA_DOC: Record<string, string> = {
  FACTURA: 'FCC',
  NOTA_CREDITO: 'NCC',
  NOTA_DEBITO: 'NDC',
};

function EncabezadoDocumento({
  d,
  numero,
  acciones,
  anulado = false,
}: {
  d: FacturaLeida['documento'];
  numero: string;
  acciones: ReactNode;
  /** 0131: el ingreso sin factura anulado o corregido. */
  anulado?: boolean;
}) {
  // El ingreso sin factura (2026-10-29) es un documento no tributario de clase OTRO.
  const sinFactura = !d.tipo_sii && d.clase_externa === 'OTRO';
  const nombre = d.tipo_sii
    ? (NOMBRE_TIPO_COMPRA[d.tipo_sii] ?? `DTE ${d.tipo_sii}`)
    : sinFactura
      ? 'Ingreso sin factura'
      : 'Documento';
  return (
    <EncabezadoDoc
      sigla={sinFactura ? 'ISF' : (SIGLA_DOC[d.clase_documento] ?? 'DOC')}
      migas="Compras › Documentos del proveedor"
      titulo={
        <>
          {nombre} N° {d.folio ?? d.numero_externo ?? '—'}
          <span className="apagado mono" style={{ fontSize: '12px', fontWeight: 500 }}>
            {numero}
          </span>
        </>
      }
      insignias={
        anulado ? (
          <span className="insignia tono-malo" data-estado="ANULADO">
            Anulado
          </span>
        ) : (
          <span
            className={`insignia ${TONO_ESTADO_DOC[d.estado] ?? 'tono-neutro'}`}
            data-estado={d.estado}
          >
            {NOMBRE_ESTADO_DOC[d.estado]}
          </span>
        )
      }
      sub={
        <>
          <b>{d.razon_social_emisor}</b> · RUT <span className="mono">{rut(d.rut_emisor)}</span> ·
          emitida el {fecha(d.fecha_emision)}
        </>
      }
      acciones={acciones}
    />
  );
}

/** Columna derecha: los datos del documento y sus totales. */
function LateralDocumento({
  d,
  impuestos,
  cargos,
  referencias,
  extra,
}: {
  d: FacturaLeida['documento'];
  impuestos: FacturaLeida['impuestos'];
  cargos?: FacturaLeida['cargos'];
  referencias?: FacturaLeida['referencias'];
  extra?: ReactNode;
}) {
  const adicionales = impuestos.filter((i) => i.tipo !== 'iva' && Number(i.monto) !== 0);
  const sinFactura = !d.tipo_sii && d.clase_externa === 'OTRO';
  // De qué documento de un tercero viene el ingreso sin factura (p. ej. la factura de Andina).
  const origen = sinFactura ? referencias?.find((r) => r.tipo === 'DOCUMENTO') : undefined;
  return (
    <>
      <Tarjeta titulo="Documento" etiqueta="Datos del documento">
        <DatosDoc
          datos={[
            {
              e: 'Tipo',
              v: d.tipo_sii
                ? (NOMBRE_TIPO_COMPRA[d.tipo_sii] ?? d.tipo_sii)
                : sinFactura
                  ? 'Ingreso sin factura (no va al SII)'
                  : 'No tributario',
            },
            origen && { e: 'Origen', v: origen.razon ?? origen.folio_texto ?? '—' },
            { e: 'Emisión', v: fecha(d.fecha_emision) },
            d.fecha_recepcion && { e: 'Recibida', v: fecha(d.fecha_recepcion) },
            {
              e: 'Entró por',
              v:
                ORIGEN_CAPTURA[d.origen_captura] ??
                d.origen_captura.toLowerCase().replace(/_/g, ' '),
            },
          ]}
        />
        {extra}
      </Tarjeta>
      <Tarjeta titulo="Totales" etiqueta="Totales del documento">
        <DatosDoc
          datos={[
            { e: 'Neto', v: pesos(d.monto_neto) },
            Number(d.monto_exento) > 0 && { e: 'Exento', v: pesos(d.monto_exento) },
            { e: 'IVA (19 %)', v: pesos(d.monto_iva) },
            ...(adicionales.length
              ? adicionales.map((i) => ({
                  e: i.impuesto_codigo.replace(/_/g, ' '),
                  v: pesos(i.monto),
                }))
              : Number(d.monto_impuestos_adicionales) > 0
                ? [{ e: 'Impuestos adicionales', v: pesos(d.monto_impuestos_adicionales) }]
                : []),
            ...(cargos ?? []).map((c) => ({
              e: `${c.tipo === 'FLETE' ? 'Flete' : c.descripcion} (en el neto)`,
              v: pesos(c.monto),
            })),
            { e: 'Total', v: pesos(d.monto_total), fuerte: true },
          ]}
        />
      </Tarjeta>
    </>
  );
}

const ORIGEN_CAPTURA: Record<string, string> = {
  DIGITADO: 'Digitada',
  DTE_XML: 'XML del proveedor',
  API: 'Registro de Compras / sistema',
};

// Aceptar la factura, resolver una diferencia o identificar una línea van sin comentario (2026-10-30):
// solo rechazar, invalidar y anular piden motivo.
type DialogoFactura = { tipo: 'rechazar' | 'invalidar' | 'anular' };

function FichaFactura({
  id,
  alAbrirRecepcion,
  alNuevaNota,
  alAbrirFactura,
  alCorregir,
}: AccionesDocumento & { id: string }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const bodegas = useBodegas(empresaId);
  const q = useFacturaCompra(empresaId, id);
  const accion = useAccionCompras(empresaId);
  const [dialogo, setDialogo] = useState<DialogoFactura | null>(null);
  const [identificando, setIdentificando] = useState<number | null>(null);
  const [imprimir, setImprimir] = useState(false);
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado pantalla">Cargando factura…</p>;
  const f = q.data;
  const d = f.documento;
  const c = f.conciliacion;
  const registrada = d.estado === 'REGISTRADA';
  // 0131: el ingreso sin factura aceptado se corrige o se anula con una firma (no con NC/ND).
  const isf = d.clase_identidad === 'NO_TRIBUTARIA';
  const anulado = !!f.invalidacion;
  const vigente = d.estado === 'ACEPTADA' && !anulado;
  const medida = !!c && c.medicion > 0;
  // Lo reclamado al proveedor no tranca: se acepta y el pago retiene ese monto hasta la NC.
  const reclamadas = c?.diferencias.filter((x) => x.estado === 'RECLAMADA') ?? [];
  const abiertas = c?.diferencias.filter((x) => x.estado === 'ABIERTA') ?? [];
  const sinGuia = abiertas.some((x) => x.tipo === 'FACTURA_SIN_RECEPCION');
  const aceptable = medida && abiertas.length === 0;
  const lineaDe = new Map(f.lineas.map((l) => [l.id, l]));
  const recepcionesRef = f.referencias.filter((r) => r.recepcion_id);
  const cerrar = { onSuccess: () => setDialogo(null) };
  const montoReclamado = reclamadas.reduce((s, x) => s + Number(x.monto ?? 0), 0);

  const conciliar = () =>
    accion.mutate({
      ruta: `/compras/facturas/${id}/conciliar`,
      cuerpo: { version: d.version, autoasignar: true },
    });
  // Rodrigo, 2026-10-30: «si se acepta se acepta y quede la trazabilidad»: sin comentario.
  const resolver = (diferenciaId: string, decision: 'aprobar' | 'reclamar') =>
    accion.mutate({ ruta: `/compras/diferencias/${diferenciaId}/${decision}`, cuerpo: {} });
  const aceptarTodas = async () => {
    for (const x of abiertas)
      await accion.mutateAsync({ ruta: `/compras/diferencias/${x.id}/aprobar`, cuerpo: {} });
  };

  const confirmarDialogo = (motivo: string) => {
    if (!dialogo) return;
    if (dialogo.tipo === 'rechazar')
      accion.mutate(
        { ruta: `/compras/facturas/${id}/rechazar`, cuerpo: { version: d.version, motivo } },
        cerrar,
      );
    else if (dialogo.tipo === 'anular')
      accion.mutate({ ruta: `/compras/facturas/${id}/anular-ingreso`, cuerpo: { motivo } }, cerrar);
    else accion.mutate({ ruta: `/compras/facturas/${id}/invalidar`, cuerpo: { motivo } }, cerrar);
  };

  const TITULOS: Record<string, [string, string, number?]> = {
    rechazar: ['Rechazar factura', 'Rechazar'],
    invalidar: ['Solicitar invalidación', 'Solicitar', 10],
    anular: ['Anular', 'Anular ingreso', 10],
  };
  const t = dialogo ? TITULOS[dialogo.tipo]! : null;

  const pasos: Paso[] = [
    { texto: 'Registrada', estado: 'hecho', ayuda: fecha(d.fecha_emision) },
    {
      texto: 'Cruzada con la guía',
      estado: medida ? (sinGuia ? 'aviso' : 'hecho') : registrada ? 'actual' : 'pendiente',
      ayuda: medida
        ? sinGuia
          ? 'hay productos sin guía'
          : `${recepcionesRef.length || 'con'} guía(s)`
        : 'Conciliar con lo recibido',
    },
    {
      texto: 'Diferencias resueltas',
      estado:
        d.estado === 'ACEPTADA' || aceptable ? 'hecho' : abiertas.length ? 'aviso' : 'pendiente',
      ayuda: abiertas.length
        ? `${abiertas.length} por resolver`
        : reclamadas.length
          ? `${reclamadas.length} reclamada(s) a la espera de NC`
          : medida
            ? 'sin diferencias'
            : undefined,
    },
    {
      texto: anulado
        ? 'Anulado'
        : d.estado === 'RECHAZADA'
          ? 'Rechazada'
          : 'Aceptada · pasa a pago',
      estado: anulado
        ? 'aviso'
        : d.estado === 'ACEPTADA'
          ? 'hecho'
          : d.estado === 'RECHAZADA'
            ? 'aviso'
            : aceptable
              ? 'actual'
              : 'pendiente',
      ayuda:
        d.estado === 'ACEPTADA'
          ? 'en Pago a Proveedores'
          : reclamadas.length
            ? `retiene ${pesos(String(montoReclamado))}`
            : undefined,
    },
  ];

  const aviso = (() => {
    if (f.invalidacion)
      return (
        <AvisoDoc tono="malo">
          <b>Anulado</b> el {fecha(f.invalidacion.en.slice(0, 10))}: {f.invalidacion.motivo}. Ya no
          se debe ni cuenta en los costos de compra.{' '}
          {f.reemplazadoPor && (
            <button
              type="button"
              className="boton-mini"
              onClick={() => alAbrirFactura(f.reemplazadoPor!, `${f.numeroVisible} corregido`)}
            >
              Ver el corregido
            </button>
          )}
        </AvisoDoc>
      );
    if (d.estado === 'RECHAZADA')
      return (
        <AvisoDoc tono="malo">
          <b>Rechazada.</b> {d.motivo_rechazo}
        </AvisoDoc>
      );
    if (d.estado === 'ACEPTADA')
      return (
        <AvisoDoc tono="ok">
          <b>Aceptada:</b> la deuda está en Pago a Proveedores.
          {reclamadas.length > 0 &&
            ` Se retienen ${pesos(String(montoReclamado))} reclamados hasta que llegue la nota de crédito.`}
        </AvisoDoc>
      );
    if (!medida)
      return (
        <AvisoDoc tono="info">
          <b>Siguiente paso:</b> cruza la factura con las guías de recepción del proveedor con{' '}
          <b>«Conciliar con lo recibido»</b>.
        </AvisoDoc>
      );
    if (sinGuia)
      return (
        <AvisoDoc tono="aviso">
          <b>La factura cobra mercadería que no tiene guía de recepción.</b> Elige qué pasó:
          <ul>
            <li>
              <b>Llegó y no se hizo guía:</b> haz la guía de recepción y vuelve a{' '}
              <b>«Conciliar con lo recibido»</b>.
            </li>
            <li>
              <b>Ya estaba en el stock</b> (por ejemplo, el stock inicial):{' '}
              <b>«Aceptar diferencia»</b>; la aprueba otra persona.
            </li>
            <li>
              <b>No llegó:</b> <b>«Pedir nota de crédito»</b>. La factura se acepta igual y ese
              monto queda retenido del pago hasta la nota de crédito.
            </li>
          </ul>
        </AvisoDoc>
      );
    if (abiertas.length)
      return (
        <AvisoDoc tono="aviso">
          <b>
            {abiertas.length === 1 ? 'Hay 1 diferencia' : `Hay ${abiertas.length} diferencias`}{' '}
            entre lo facturado y lo recibido.
          </b>{' '}
          Acéptala si está bien (la aprueba otra persona) o reclámala al proveedor: la factura se
          acepta igual y ese monto queda retenido hasta la nota de crédito.
        </AvisoDoc>
      );
    return (
      <AvisoDoc tono="ok">
        <b>Todo cuadra.</b> Acepta la factura para dejar la deuda en Pago a Proveedores.
        {reclamadas.length > 0 &&
          ` Quedarán retenidos ${pesos(String(montoReclamado))} reclamados hasta la nota de crédito.`}
      </AvisoDoc>
    );
  })();

  const acciones = (
    <>
      <button
        type="button"
        disabled={imprimir}
        title="Comprobante del ingreso para imprimir o guardar como PDF"
        onClick={() => setImprimir(true)}
      >
        {imprimir ? 'Preparando…' : 'Imprimir comprobante'}
      </button>
      {registrada && puede('compras.factura.conciliar') && (
        <button type="button" disabled={accion.isPending} onClick={conciliar}>
          Conciliar con lo recibido
        </button>
      )}
      {registrada && puede('compras.factura.aceptar') && (
        <button type="button" onClick={() => setDialogo({ tipo: 'rechazar' })}>
          Rechazar
        </button>
      )}
      {registrada && puede('compras.factura.aceptar') && (
        <button
          type="button"
          className="primario"
          disabled={accion.isPending || !aceptable}
          title={
            !aceptable
              ? 'Primero concilia y resuelve las diferencias (acéptalas o reclámalas al proveedor)'
              : reclamadas.length
                ? 'Se acepta con lo reclamado esperando la NC: ese monto queda retenido del pago'
                : undefined
          }
          onClick={() =>
            accion.mutate({
              ruta: `/compras/facturas/${id}/aceptar`,
              cuerpo: { version: d.version },
            })
          }
        >
          {reclamadas.length ? 'Aceptar factura (con reclamo, espera NC)' : 'Aceptar factura'}
        </button>
      )}
      {isf && vigente && puede('compras.factura.invalidar') && (
        <button
          type="button"
          title="Sacar el ingreso: sale el stock que entró y la deuda con el proveedor"
          onClick={() => setDialogo({ tipo: 'anular' })}
        >
          Anular ingreso
        </button>
      )}
      {isf && vigente && puede('compras.factura.invalidar') && alCorregir && (
        <button
          type="button"
          className="primario"
          title="Cambiar precios, descuentos, flete o impuestos: el stock no se mueve"
          onClick={() => alCorregir(id, `Corregir ${f.numeroVisible}`)}
        >
          Corregir ingreso
        </button>
      )}
      {!isf && vigente && puede('compras.factura.invalidar') && (
        <button type="button" onClick={() => setDialogo({ tipo: 'invalidar' })}>
          Solicitar invalidación
        </button>
      )}
      {!isf && vigente && puede('compras.factura.crear') && (
        <button type="button" className="primario" onClick={() => alNuevaNota(id)}>
          Registrar NC / ND
        </button>
      )}
    </>
  );

  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="factura-compra">
      <EncabezadoDocumento d={d} numero={f.numeroVisible} acciones={acciones} anulado={anulado} />
      {imprimir && <ImprimirComprobante f={f} alTerminar={() => setImprimir(false)} />}
      <CaminoDoc pasos={pasos} />
      {f.corrigeA && (
        <AvisoDoc tono="info">
          Este ingreso <b>corrige</b> a uno anulado con el mismo número.{' '}
          <button
            type="button"
            className="boton-mini"
            onClick={() => alAbrirFactura(f.corrigeA!, `${f.numeroVisible} anulado`)}
          >
            Ver el anulado
          </button>
        </AvisoDoc>
      )}
      {aviso}
      {accion.isError && !dialogo && (
        <div className="doc-aviso tono-malo">
          <PanelError error={accion.error} />
        </div>
      )}
      <CuerpoDoc
        principal={
          <>
            <Tarjeta
              titulo="Detalle de la factura"
              extra={`${f.lineas.length} ${f.lineas.length === 1 ? 'línea' : 'líneas'}`}
            >
              <table className="grilla compacta" aria-label="Líneas de la factura">
                <thead>
                  <tr>
                    <th style={{ width: '2.5rem' }}>N°</th>
                    <th>Descripción</th>
                    <th style={{ width: '8rem' }}>Producto</th>
                    <th className="der">Cantidad</th>
                    <th className="der">Precio</th>
                    <th className="der">Neto</th>
                    <th style={{ width: '9rem' }}>Guía</th>
                  </tr>
                </thead>
                <tbody>
                  {f.lineas.map((l) => {
                    const sku = l.identificacion?.sku ?? l.sku;
                    return (
                      <tr key={l.id}>
                        <td>{l.numero}</td>
                        <td>{l.descripcion}</td>
                        <td className="mono">
                          {sku ?? (
                            <>
                              <span className="tono-malo">sin identificar</span>{' '}
                              {registrada && puede('compras.factura.editar') && (
                                <button
                                  type="button"
                                  className="boton-mini"
                                  onClick={() => setIdentificando(l.numero)}
                                >
                                  Identificar
                                </button>
                              )}
                            </>
                          )}
                        </td>
                        <td className="der">{decimal(l.cantidad)}</td>
                        <td className="der">{pesos(l.precio_unitario)}</td>
                        <td className="der">
                          <b>{pesos(l.neto)}</b>
                        </td>
                        <td>
                          {l.facturacion === 'TOTAL' ? (
                            <span className="insignia tono-ok">Con guía</span>
                          ) : l.facturacion === 'PARCIAL' ? (
                            <span className="insignia tono-aviso">
                              Parcial ({decimal(l.asignadoDocumento)})
                            </span>
                          ) : (
                            <span className="insignia tono-aviso">Sin guía</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Tarjeta>
            <Tarjeta
              titulo="Conciliación con lo recibido"
              etiqueta="Conciliación"
              extra={
                <>
                  {c ? SITUACION[c.situacion] : 'Sin conciliar'}
                  {medida && <span className="apagado"> · medición {c!.medicion}</span>}
                  {registrada && abiertas.length > 1 && puede('compras.diferencia.aprobar') && (
                    <button
                      type="button"
                      className="boton-mini"
                      disabled={accion.isPending}
                      onClick={() => void aceptarTodas().catch(() => undefined)}
                    >
                      Aceptar todas ({abiertas.length})
                    </button>
                  )}
                </>
              }
            >
              {c && c.diferencias.length > 0 ? (
                <table className="grilla compacta" aria-label="Diferencias">
                  <thead>
                    <tr>
                      <th>Qué pasa</th>
                      <th>Línea</th>
                      <th className="der">Recibido</th>
                      <th className="der">Facturado</th>
                      <th className="der">Diferencia</th>
                      <th>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {c.diferencias.map((x) => {
                      const [estadoTxt, tono] = ESTADO_DIFERENCIA[x.estado] ?? [
                        x.estado.toLowerCase(),
                        'tono-neutro',
                      ];
                      return (
                        <tr key={x.id}>
                          <td>
                            {TIPO_DIFERENCIA[x.tipo] ?? x.tipo.toLowerCase().replace(/_/g, ' ')}
                          </td>
                          <td>
                            {x.documento_linea_id
                              ? lineaDe.get(x.documento_linea_id)?.descripcion
                              : '—'}
                          </td>
                          <td className="der">{decimal(x.esperado)}</td>
                          <td className="der">{decimal(x.documentado)}</td>
                          <td className="der">
                            <b>{x.monto ? pesos(x.monto) : decimal(x.delta)}</b>
                          </td>
                          <td>
                            <span className={`insignia ${tono}`}>{estadoTxt}</span>
                          </td>
                          <td>
                            {(x.estado === 'ABIERTA' || x.estado === 'RECLAMADA') &&
                              registrada &&
                              puede('compras.diferencia.aprobar') && (
                                <span className="acciones-fila">
                                  <button
                                    type="button"
                                    className="boton-mini"
                                    disabled={accion.isPending}
                                    onClick={() => resolver(x.id, 'aprobar')}
                                  >
                                    Aceptar diferencia
                                  </button>
                                  {x.estado === 'ABIERTA' && (
                                    <button
                                      type="button"
                                      className="boton-mini"
                                      disabled={accion.isPending}
                                      onClick={() => resolver(x.id, 'reclamar')}
                                    >
                                      Pedir nota de crédito
                                    </button>
                                  )}
                                </span>
                              )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p className="vacio-tarjeta">
                  {medida
                    ? 'Sin diferencias: lo facturado cuadra con lo recibido.'
                    : 'Todavía no se cruza con las guías de recepción.'}
                </p>
              )}
            </Tarjeta>
            <Tarjeta titulo="Historia" etiqueta="Historia">
              <Historia transiciones={f.transiciones} />
            </Tarjeta>
          </>
        }
        lateral={
          <LateralDocumento
            d={d}
            impuestos={f.impuestos}
            cargos={f.cargos}
            referencias={f.referencias}
            extra={
              recepcionesRef.length > 0 && (
                <p className="acciones-fila" style={{ marginTop: '8px' }}>
                  <span className="apagado">Guías:</span>
                  {recepcionesRef.map((r, i) => (
                    <button
                      key={r.recepcion_id}
                      type="button"
                      className="enlace"
                      onClick={() => alAbrirRecepcion(r.recepcion_id!, 'Recepción')}
                    >
                      {r.folio_texto ?? `abrir recepción ${i + 1}`}
                    </button>
                  ))}
                </p>
              )
            }
          />
        }
      />
      {dialogo && t && (
        <DialogoMotivo
          titulo={`${t[0]} ${f.numeroVisible}`}
          textoBoton={t[1]}
          minimo={t[2] ?? 3}
          explicacion={
            dialogo.tipo === 'invalidar'
              ? 'La invalidación la aprueba OTRA persona en la bandeja de aprobaciones de Compras.'
              : dialogo.tipo === 'anular'
                ? 'Sale del stock lo que entró con este ingreso y la deuda con el proveedor; el número queda libre para ingresarlo de nuevo. Si solo hay que cambiar precios o descuentos, usa «Corregir ingreso».'
                : undefined
          }
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={confirmarDialogo}
          alCerrar={() => setDialogo(null)}
        />
      )}
      {identificando !== null && (
        <BuscadorProducto
          empresaId={empresaId}
          listaId={null}
          ubicacionId={bodegas.lista[0]?.id ?? ''}
          ubicaciones={bodegas.lista}
          alElegir={(p) => {
            accion.mutate({
              ruta: `/compras/facturas/${id}/lineas/${identificando}/identificar`,
              cuerpo: {
                version: d.version,
                productoId: p.producto.id,
                presentacionId: p.presentacion.id,
              },
            });
            setIdentificando(null);
          }}
          alCerrar={() => setIdentificando(null)}
        />
      )}
    </div>
  );
}

// ================================================================ ficha de la NC / ND

interface Imputacion {
  clave: string;
  notaLineaNumero: number;
  efecto: string;
  facturaId: string;
  monto: string;
  motivo: string;
  /** PRECIO/DESCUENTO: la línea recibida; DEVOLUCIÓN: la línea devuelta; FLETE: el cargo. */
  recepcionLineaId?: string;
  devolucionLineaId?: string;
  cargoNumero?: string;
  cantidadBase?: string;
}

/** Lo que el efecto necesita nombrar, según la factura elegida o las devoluciones del proveedor. */
function DetalleImputacion({
  x,
  proveedorId,
  deshabilitado,
  alCambiar,
}: {
  x: Imputacion;
  proveedorId: string | null;
  deshabilitado: boolean;
  alCambiar: (cambios: Partial<Imputacion>) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const necesitaFactura = x.efecto === 'PRECIO' || x.efecto === 'DESCUENTO' || x.efecto === 'FLETE';
  const factura = useFacturaCompra(empresaId, necesitaFactura && x.facturaId ? x.facturaId : null);
  const devoluciones = useDevoluciones(empresaId, { estado: 'CONFIRMADA' });
  const [devolucionId, setDevolucionId] = useState('');
  const devolucion = useDevolucion(
    empresaId,
    x.efecto === 'DEVOLUCION' && devolucionId ? devolucionId : null,
  );
  const cantidad = (
    <input
      className="entrada-numero"
      aria-label={`Unidades base línea ${x.notaLineaNumero}`}
      placeholder="unid. base"
      disabled={deshabilitado}
      value={x.cantidadBase ?? ''}
      onChange={(e) => alCambiar({ cantidadBase: e.target.value })}
    />
  );
  if (x.efecto === 'PRECIO' || x.efecto === 'DESCUENTO') {
    const f = factura.data;
    const lineaDe = new Map((f?.lineas ?? []).map((l) => [l.id, l]));
    const asignadas = (f?.asignaciones ?? []).filter((a) => !a.reversada);
    return (
      <span className="acciones-fila">
        <select
          aria-label={`Línea recibida línea ${x.notaLineaNumero}`}
          disabled={deshabilitado || !f}
          value={x.recepcionLineaId ?? ''}
          onChange={(e) => {
            const a = asignadas.find((y) => y.recepcion_linea_id === e.target.value);
            alCambiar({
              recepcionLineaId: e.target.value,
              ...(a ? { cantidadBase: a.cantidad_base } : {}),
            });
          }}
        >
          <option value="">línea recibida…</option>
          {asignadas.map((a) => (
            <option key={a.id} value={a.recepcion_linea_id}>
              {lineaDe.get(a.documento_linea_id)?.descripcion ?? 'línea'} ·{' '}
              {decimal(a.cantidad_base)}
            </option>
          ))}
        </select>
        {cantidad}
      </span>
    );
  }
  if (x.efecto === 'FLETE')
    return (
      <select
        aria-label={`Cargo línea ${x.notaLineaNumero}`}
        disabled={deshabilitado || !factura.data}
        value={x.cargoNumero ?? ''}
        onChange={(e) => alCambiar({ cargoNumero: e.target.value })}
      >
        <option value="">cargo de flete…</option>
        {(factura.data?.cargos ?? [])
          .filter((c) => c.tipo === 'FLETE')
          .map((c) => (
            <option key={c.numero} value={String(c.numero)}>
              {c.descripcion} · {pesos(c.monto)}
            </option>
          ))}
      </select>
    );
  if (x.efecto === 'DEVOLUCION') {
    const propias = (devoluciones.data?.items ?? []).filter(
      (d) => !proveedorId || d.proveedor_entidad_id === proveedorId,
    );
    return (
      <span className="acciones-fila">
        <select
          aria-label={`Devolución línea ${x.notaLineaNumero}`}
          disabled={deshabilitado}
          value={devolucionId}
          onChange={(e) => setDevolucionId(e.target.value)}
        >
          <option value="">devolución…</option>
          {propias.map((d) => (
            <option key={d.id} value={d.id}>
              {d.numero ? `DEV ${d.numero}` : d.id.slice(0, 8)} · {fecha(d.fecha_negocio)}
            </option>
          ))}
        </select>
        <select
          aria-label={`Línea devuelta línea ${x.notaLineaNumero}`}
          disabled={deshabilitado || !devolucion.data}
          value={x.devolucionLineaId ?? ''}
          onChange={(e) => {
            const l = devolucion.data?.lineas.find((y) => y.id === e.target.value);
            alCambiar({
              devolucionLineaId: e.target.value,
              ...(l ? { cantidadBase: l.cantidad_base } : {}),
            });
          }}
        >
          <option value="">línea…</option>
          {(devolucion.data?.lineas ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              N° {l.numero} · {decimal(l.cantidad_base)} unid.
            </option>
          ))}
        </select>
        {cantidad}
      </span>
    );
  }
  return <span className="apagado">solo la deuda</span>;
}

function FichaNota({ id, alAbrirFactura }: AccionesDocumento & { id: string }) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const q = useNotaCompra(empresaId, id);
  const accion = useAccionCompras(empresaId);
  const facturas = useFacturasCompra(empresaId, {
    estado: 'ACEPTADA',
    proveedorEntidadId: q.data?.documento.proveedor_entidad_id ?? undefined,
  });
  const [imputaciones, setImputaciones] = useState<Imputacion[] | null>(null);
  const [dialogo, setDialogo] = useState<'aceptar' | 'rechazar' | null>(null);
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <p className="apagado pantalla">Cargando nota…</p>;
  const n: NotaLeida = q.data;
  const d = n.documento;
  const registrada = d.estado === 'REGISTRADA';
  // Aceptar o rechazar una NC/ND lo firma quien tiene SU permiso (no el de facturas).
  const permisoAceptarNota =
    n.clase === 'NOTA_DEBITO' ? 'compras.nd.aceptar' : 'compras.nc.aceptar';
  const facturasProveedor = (facturas.data?.items ?? []).filter(
    (x) => x.tipo_sii !== null && [30, 32, 33, 34, 46].includes(x.tipo_sii),
  );
  const refFolio = n.referencias.find((r) => r.tipo === 'DOCUMENTO')?.folio_texto;
  const facturaRef =
    facturasProveedor.find((x) => x.folio === refFolio)?.id ?? facturasProveedor[0]?.id ?? '';
  const editor: Imputacion[] =
    imputaciones ??
    n.lineas.map((l) => ({
      clave: l.id,
      notaLineaNumero: l.numero,
      efecto: n.clase === 'NOTA_CREDITO' ? 'FINANCIERA' : 'PRECIO',
      facturaId: facturaRef,
      monto: l.neto,
      motivo: l.descripcion,
    }));
  const set = (clave: string, campo: keyof Imputacion, v: string) =>
    setImputaciones(editor.map((x) => (x.clave === clave ? { ...x, [campo]: v } : x)));
  const cambiar = (clave: string, c: Partial<Imputacion>) =>
    setImputaciones(editor.map((x) => (x.clave === clave ? { ...x, ...c } : x)));
  const efectos =
    n.clase === 'NOTA_CREDITO'
      ? EFECTOS_NOTA
      : (['PRECIO', 'FLETE', 'TRIBUTARIA', 'FINANCIERA'] as const);

  const imputar = () =>
    accion.mutate({
      ruta: `/compras/notas/${id}/imputar`,
      cuerpo: {
        version: d.version,
        imputaciones: editor.map((x) => ({
          notaLineaNumero: x.notaLineaNumero,
          efecto: x.efecto,
          ...(x.facturaId ? { facturaId: x.facturaId } : {}),
          ...((x.efecto === 'PRECIO' || x.efecto === 'DESCUENTO') && x.recepcionLineaId
            ? { recepcionLineaId: x.recepcionLineaId }
            : {}),
          ...(x.efecto === 'DEVOLUCION' && x.devolucionLineaId
            ? { devolucionLineaId: x.devolucionLineaId }
            : {}),
          ...(x.efecto === 'FLETE' && x.cargoNumero
            ? { facturaCargoNumero: Number(x.cargoNumero) }
            : {}),
          ...(['PRECIO', 'DESCUENTO', 'DEVOLUCION'].includes(x.efecto) && x.cantidadBase
            ? { cantidadBase: cuatroDecimales(x.cantidadBase) }
            : {}),
          montoNeto: entradaDecimal(x.monto, 0) ?? x.monto,
          motivo: x.motivo.trim().length >= 3 ? x.motivo.trim() : 'corrección del proveedor',
        })),
      },
    });

  const esNc = n.clase === 'NOTA_CREDITO';
  const imputada = n.situacion === 'CONCILIADA';
  const pasos: Paso[] = [
    { texto: 'Registrada', estado: 'hecho', ayuda: fecha(d.fecha_emision) },
    {
      texto: 'Imputada',
      estado: imputada || !registrada ? 'hecho' : 'actual',
      ayuda: imputada
        ? 'cada línea dice qué corrige'
        : Number(n.sinImputar) > 0
          ? `falta imputar ${pesos(n.sinImputar)}`
          : 'qué corrige cada línea',
    },
    {
      texto: d.estado === 'RECHAZADA' ? 'Rechazada' : 'Aceptada',
      estado:
        d.estado === 'ACEPTADA'
          ? 'hecho'
          : d.estado === 'RECHAZADA'
            ? 'aviso'
            : imputada
              ? 'actual'
              : 'pendiente',
      ayuda: esNc ? 'rebaja la deuda y libera lo retenido' : 'suma a la deuda',
    },
  ];
  const acciones = (
    <>
      {registrada && puede('compras.factura.conciliar') && (
        <button type="button" disabled={accion.isPending} onClick={imputar}>
          Imputar
        </button>
      )}
      {registrada && puede(permisoAceptarNota) && (
        <button type="button" onClick={() => setDialogo('rechazar')}>
          Rechazar
        </button>
      )}
      {registrada && puede(permisoAceptarNota) && (
        <button
          type="button"
          className="primario"
          disabled={accion.isPending || n.situacion !== 'CONCILIADA'}
          onClick={() => setDialogo('aceptar')}
        >
          Aceptar nota
        </button>
      )}
    </>
  );

  return (
    <div className="pantalla documento-compra doc-ficha" data-pantalla="nota-compra">
      <EncabezadoDocumento d={d} numero={n.numeroVisible} acciones={acciones} />
      <CaminoDoc pasos={pasos} />
      {d.estado === 'RECHAZADA' ? (
        <AvisoDoc tono="malo">
          <b>Rechazada.</b> {d.motivo_rechazo}
        </AvisoDoc>
      ) : d.estado === 'ACEPTADA' ? (
        <AvisoDoc tono="ok">
          <b>Aceptada:</b>{' '}
          {esNc
            ? 'rebajó la deuda con el proveedor y liberó lo retenido de su factura.'
            : 'quedó sumada a la deuda con el proveedor.'}
        </AvisoDoc>
      ) : imputada ? (
        <AvisoDoc tono="ok">
          <b>Imputada completa.</b> Acepta la nota para cerrar la corrección.
        </AvisoDoc>
      ) : (
        <AvisoDoc tono="info">
          <b>Siguiente paso:</b> di qué corrige cada línea (la factura y el efecto) y aprieta{' '}
          <b>«Imputar»</b>.{Number(n.sinImputar) > 0 && <> Falta imputar {pesos(n.sinImputar)}.</>}
        </AvisoDoc>
      )}
      {accion.isError && !dialogo && (
        <div className="doc-aviso tono-malo">
          <PanelError error={accion.error} />
        </div>
      )}
      <CuerpoDoc
        principal={
          <>
            <Tarjeta
              titulo="Qué corrige cada línea"
              etiqueta="Imputación"
              extra={
                imputada ? (
                  <span className="insignia tono-ok">Conciliada</span>
                ) : n.situacion === 'SIN_IMPUTAR' ? (
                  <span className="insignia tono-aviso">Sin imputar</span>
                ) : (
                  <span className="insignia tono-aviso">Pendiente</span>
                )
              }
            >
              <table className="grilla compacta" aria-label="Imputación de la nota">
                <thead>
                  <tr>
                    <th>Línea</th>
                    <th>Descripción</th>
                    <th>Efecto</th>
                    <th>Factura</th>
                    <th>Detalle</th>
                    <th className="der">Neto</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {editor.map((x) => {
                    const l = n.lineas.find((y) => y.numero === x.notaLineaNumero);
                    return (
                      <tr key={x.clave}>
                        <td>{x.notaLineaNumero}</td>
                        <td>{l?.descripcion}</td>
                        <td>
                          <select
                            aria-label={`Efecto línea ${x.notaLineaNumero}`}
                            disabled={!registrada}
                            value={x.efecto}
                            onChange={(e) => set(x.clave, 'efecto', e.target.value)}
                          >
                            {efectos.map((ef) => (
                              <option key={ef} value={ef}>
                                {ef.toLowerCase()}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            aria-label={`Factura línea ${x.notaLineaNumero}`}
                            disabled={!registrada}
                            value={x.facturaId}
                            onChange={(e) => set(x.clave, 'facturaId', e.target.value)}
                          >
                            <option value="">—</option>
                            {facturasProveedor.map((fa) => (
                              <option key={fa.id} value={fa.id}>
                                {fa.numero_visible ?? fa.folio}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <DetalleImputacion
                            x={x}
                            proveedorId={d.proveedor_entidad_id}
                            deshabilitado={!registrada}
                            alCambiar={(c) => cambiar(x.clave, c)}
                          />
                        </td>
                        <td className="der">
                          <input
                            className="entrada-numero"
                            aria-label={`Monto línea ${x.notaLineaNumero}`}
                            disabled={!registrada}
                            value={x.monto}
                            onChange={(e) => set(x.clave, 'monto', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            aria-label={`Motivo línea ${x.notaLineaNumero}`}
                            disabled={!registrada}
                            value={x.motivo}
                            onChange={(e) => set(x.clave, 'motivo', e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="apagado">
                FINANCIERA o TRIBUTARIA corrigen solo la deuda; PRECIO o DESCUENTO, el costo de lo
                recibido; FLETE, el cargo de la factura; DEVOLUCIÓN, contra la devolución o el
                faltante confirmado.
              </p>
              {n.imputaciones.length > 0 && (
                <p className="apagado">
                  Imputación vigente:{' '}
                  {n.imputaciones
                    .map((i) => `${i.efecto.toLowerCase()} ${pesos(i.monto_neto)}`)
                    .join(' · ')}
                  {n.imputaciones[0]?.factura_id && (
                    <>
                      {' '}
                      ·{' '}
                      <button
                        type="button"
                        className="enlace"
                        onClick={() => alAbrirFactura(n.imputaciones[0]!.factura_id!, 'Factura')}
                      >
                        abrir factura
                      </button>
                    </>
                  )}
                </p>
              )}
            </Tarjeta>
            <Tarjeta titulo="Historia" etiqueta="Historia">
              <Historia transiciones={n.transiciones} />
            </Tarjeta>
          </>
        }
        lateral={<LateralDocumento d={d} impuestos={n.impuestos} />}
      />
      {dialogo && (
        <DialogoMotivo
          titulo={`${dialogo === 'aceptar' ? 'Aceptar' : 'Rechazar'} ${n.numeroVisible}`}
          textoBoton={dialogo === 'aceptar' ? 'Aceptar (firma)' : 'Rechazar'}
          pendiente={accion.isPending}
          error={accion.isError ? accion.error : null}
          alConfirmar={(motivo) =>
            accion.mutate(
              { ruta: `/compras/notas/${id}/${dialogo}`, cuerpo: { version: d.version, motivo } },
              { onSuccess: () => setDialogo(null) },
            )
          }
          alCerrar={() => setDialogo(null)}
        />
      )}
    </div>
  );
}

/** El número de OC de una referencia (para mostrar en listas). */
export function NumeroOc({ ordenId }: { ordenId: string }) {
  const { empresa } = useSesion();
  const q = useOrdenCompra(empresa!.id, ordenId);
  return <>{q.data?.numeroVisible ?? '…'}</>;
}
